import { Avatar, CareTile, Icon } from '../common';
import { seedSelection, toggleDog, type CareKind, type SeedableDog } from './careSelection';
import type { OwnerDog } from './types';

/**
 * A care flag, and which dogs it covers (per-dog-care-flags).
 *
 * The tile stays the switch; the disclosure beneath it says who. It is the
 * SAME `.perdog` slide the `Area · Other` control uses — the contract is
 * `docs/design/per-dog-care-flags.html`, and reusing that markup is why this
 * reads as the tile continuing rather than a second kind of control arriving.
 */
export interface CarePickerProps {
  kind: CareKind;
  on: boolean;
  onToggle: (on: boolean) => void;
  /** The dogs this stay covers, in the household's own order. */
  dogs: OwnerDog[];
  selected: string[];
  onSelect: (ids: string[]) => void;
  icon: string;
  title: string;
  sub: string;
  /** Medication only: the dog's own instructions, and the way to change them. */
  instructions?: (dog: OwnerDog) => React.ReactNode;
  /** The contact who gave us this value, per dog — `dogs.confirmed_by`. */
  markFor?: (dog: OwnerDog) => React.ReactNode;
}

/**
 * The tile and its panel, in ONE column (Freddi, 2026-09-21: "so if medication
 * checked, the slider opens directly under with the dogs to check … using 3
 * columns rather than just stacking in a single column").
 *
 * A first pass moved the panel out to span the card, because in-column it got a
 * third of the width and a single-line instructions input clipped "Half a
 * Loxicom tablet" to nothing. That solved the wrong half: what a panel gains by
 * spanning the card it loses in saying WHICH tile it belongs to, and three
 * stacked full-width panels say it least of all.
 *
 * The column stays. The input becomes a textarea that WRAPS — a narrow column
 * wants text that folds, not text that runs off the end.
 */
export function CarePicker({
  kind, on, onToggle, dogs, selected, onSelect, icon, title, sub, instructions, markFor,
}: CarePickerProps) {
  const order = dogs.map((d) => d.id);
  const one = dogs.length === 1;
  const none = dogs.length === 0;

  // ONE DOG NEEDS NO LIST, and no dogs needs no tile. Asking *which* of one is
  // a question with a single possible answer; asking it of none is a flag that
  // can never name anybody, and the fix for that is to add the dog.
  const listed = on && !one && !none;
  // Medication still opens on a one-dog household: WHAT is a real question when
  // WHO is not.
  const open = listed || (on && one && !!instructions);

  const setOn = (next: boolean) => {
    onToggle(next);
    onSelect(next ? seedSelection(kind, dogs as SeedableDog[]) : []);
  };

  const pick = (dogId: string) => {
    const next = toggleDog(selected, dogId, order);
    onSelect(next);
    // THE TILE TURNS ITSELF OFF when the last dog goes. A flag on for nobody is
    // the state that reaches the staff brief naming no one, which is the whole
    // thing this feature exists to end — so it cannot be reachable by unticking.
    if (next.length === 0) onToggle(false);
  };

  return (
    <div className="care-pick">
      <CareTile
        on={on}
        onToggle={none ? () => undefined : setOn}
        tone={kind === 'food' ? undefined : kind}
        icon={icon}
        title={title}
        sub={none ? 'No dogs on this household yet' : on && dogs.length ? namesOf(dogs, selected) : sub}
      />
      <div className={'perdog' + (open ? ' open' : '')}>
        <div className="perdog-in">
          {/* ONE DOG: no rows to carry the mark, so it rides the heading — the
              same rule as a row: only on a ticked value (2026-10-08). */}
          <div className="perdog-h">
            {headingFor(kind, one, dogs)}
            {one && on && selected.includes(dogs[0]!.id) ? markFor?.(dogs[0]!) : null}
          </div>
          {listed &&
            dogs.map((d) => {
              const picked = selected.includes(d.id);
              return (
                <div key={d.id}>
                  {/* THE WHOLE ROW IS THE TARGET, as a real button: an 18px box
                      is a 4mm trackpad target and the dog beside it is what the
                      eye was aiming at anyway. `aria-pressed` and the keyboard
                      come free; a div with a handler would have neither. */}
                  <button type="button" className="drow" aria-pressed={picked} onClick={() => pick(d.id)}>
                    <span className={'dck ' + kind + (picked ? ' on' : '')} aria-hidden="true">
                      <Icon name="check" size={11} />
                    </span>
                    <span className="dname">
                      <Avatar name={d.name} id={d.id} kind="dog" size={26} />
                      {/* The name in its own box: as a bare text node in the
                          column it could not take an ellipsis, and a mark
                          beside it clipped it mid-letter. */}
                      <span className="nm"><span className="nm-t">{d.name}</span>{d.breed ? <i>{d.breed}</i> : null}</span>
                    </span>
                    {/* Only where the CUSTOMER gave us the value, and never on a
                        row that is not ticked — a mark claims *they told us
                        this* about a value that is not there to read. */}
                    {picked ? markFor?.(d) : null}
                  </button>
                  {picked && instructions ? <div className="dmeds">{instructions(d)}</div> : null}
                </div>
              );
            })}
          {one && instructions ? <div className="dmeds one">{instructions(dogs[0]!)}</div> : null}
        </div>
      </div>
    </div>
  );
}

/** The tile's sub once it is on: who it covers, in the household's order. */
function namesOf(dogs: OwnerDog[], selected: string[]): string {
  const picked = dogs.filter((d) => selected.includes(d.id));
  if (picked.length === 0) return '—';
  if (picked.length === dogs.length && dogs.length > 1) return `All ${dogs.length} dogs`;
  return picked.map((d) => d.name).join(', ');
}

function headingFor(kind: CareKind, one: boolean, dogs: OwnerDog[]): string {
  if (one) return `What ${dogs[0]?.name ?? 'this dog'} takes`;
  return kind === 'food' ? 'Whose food is here' : kind === 'meds' ? 'Which dogs need meds' : 'Which dogs stay apart';
}
